import axios from 'axios';
import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import XHR from 'i18next-xhr-backend';
import 'intersection-observer'; // intersection-observer polyfill
import moment from 'moment-timezone';
import React from 'react';
import { createRoot } from 'react-dom/client';
import styled, { createGlobalStyle } from 'styled-components';
import bugsnag from './bugsnag';
import resourcesToBackend from 'i18next-resources-to-backend';

import i18next from 'i18next';
import './firebase.ts';
import { initReactI18next } from 'react-i18next';

import App from './App';
import FiveHunna from './pages/Error/FiveHunna';
import { getAuth, onAuthStateChanged, signInAnonymously } from 'firebase/auth';
import { TrackingProvider } from './contexts/TrackingContext';

const GlobalStyle = createGlobalStyle`
  html, body {
    color: #1c1c1c;
    font-family: 'DM Sans', sans-serif;
    background-color: #FCFCFC;
    height: 100%;
    margin: 0px;

    #root {
      width: 100%;
      height: 100%;
      display: flex;
      min-height: 100%;
      flex-direction: column;
    }
    

    & * {
      box-sizing: border-box;
    }

    .container {
      max-width: 1152px;
      margin: 0 auto;
      padding: 16px;
    }

    h1 {
      font-size: 32px;
      font-weight: 300;
      margin-top: ${((32 / 8) * 2) / 10}em;
      margin-bottom: ${((32 / 8) * 2) / 10}em;

      @media screen and (min-width: 768px) {
        font-size: 40px;
        margin-top: ${((40 / 8) * 2) / 10}em;
        margin-bottom: ${((40 / 8) * 2) / 10}em;
      }
    }

    h2 {
      font-size: ${32 * 0.8}px;
      font-weight: 300;
      margin-top: ${(((32 * 0.8) / 8) * 2) / 10}em;
      margin-bottom: ${(((32 * 0.8) / 8) * 2) / 10}em;

      @media screen and (min-width: 768px) {
        font-size: 32px;
        margin-top: ${((32 / 8) * 2) / 10}em;
        margin-bottom: ${((32 / 8) * 2) / 10}em;
      }
    }

    h3 {
      font-size: ${24 * 0.8}px;
      font-weight: 300;
      margin-top: ${(((24 * 0.8) / 8) * 2) / 10}em;
      margin-bottom: ${(((24 * 0.8) / 8) * 2) / 10}em;

      @media screen and (min-width: 768px) {
        font-size: 24px;
        margin-top: ${((24 / 8) * 2) / 10}em;
        margin-bottom: ${((24 / 8) * 2) / 10}em;
      }
    }

    h4 {
      font-size: ${20 * 0.8}px;
      font-weight: 500;
      margin-top: ${(((20 * 0.8) / 8) * 2) / 10}em;
      margin-bottom: ${(((20 * 0.8) / 8) * 2) / 10}em;

      @media screen and (min-width: 768px) {
        font-size: 20px;
        margin-top: ${((22 / 8) * 2) / 10}em;
        margin-bottom: ${((22 / 8) * 2) / 10}em;
      }
    }

    p {
      letter-spacing: ${((100 / 64) * 2) / 10}px;
      line-height: ${(100 / 16) * 4}px;
      font-weight: 300;
      font-size: 14px;

      @media screen and (min-width: 768px) {
        font-size: 16px;
        letter-spacing: ${((100 / 64) * 3) / 10}px;
      }
    }

    strong {
      font-weight: 500;
    }

    a {
      text-decoration: none;
      color: #FF6E3D;

      &:hover {
        cursor: pointer;
      }
    }

    button {
      &:hover {
        cursor: pointer;
      }
    }

    label {
      display: block;
      font-size: 12px;
      font-weight: 500;
      text-transform: uppercase;
      color: #9B9B9B;
    }

    input:not(.MuiInputBase-input):not(.MuiFilledInput-input) {
      width: 100%;
      height: 100%;
      border: 0px;
      font-size: 14px;
      border-radius: 3px;
      font-family: 'DM Sans', sans-serif;

      ::placeholder {
        opacity: 0.7;
      }

      &[type=text], &[type=email], &[type=number], &[type=tel] {
        font-size: 16px;
        background-color: #fff;
        border: 1px solid #e9eef3;
        padding: 24px 16px;
        letter-spacing: .333px;
        font-weight: 300;
      }
    }

    input, button {
      outline: none !important;
    }

    select {
      /* for Firefox */
      -moz-appearance: none;
      /* for Chrome */
      -webkit-appearance: none;
    }

    /* For IE10 */

    select::-ms-expand {
      display: none;
    }
  }

  @keyframes common-fade-in {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

moment.tz.setDefault('Atlantic/Reykjavik');
moment.locale('en');

axios.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error && error.response && error.response.status === 429) {
      const nextTry = moment().add(1, 'minute').format('HH:mm:ss');
      alert(
        `We've detected too many requests from your computer. Please try again at ${nextTry}`,
      );
    }
    return Promise.reject(error);
  },
);
const auth = getAuth();
onAuthStateChanged(auth, (u) => {
  if (!u) {
    console.log('Loggin in anon');
    // Don't navigate away on failure: Safari's tracking protection (e.g. in
    // private browsing) can block this request, and bouncing to '/' would
    // throw the guest off the page they opened.
    signInAnonymously(auth).catch((error) => {
      bugsnag.notify(error);
    });
  } else {
    console.log('Current user: ' + u.uid);
    console.log('Is anon: ' + u.isAnonymous);
  }
});

i18next
  .use(XHR)
  .use(LanguageDetector)
  .use(initReactI18next)
  .use(
    resourcesToBackend(
      (language: string, namespace: string) =>
        import(`./locales/${language}/${namespace}.${language}.json`),
    ),
  )
  .init({
    debug: import.meta.env.VITE_NODE_ENV !== 'production',
    fallbackLng: 'en',
    interpolation: {
      escapeValue: false,
    },
    initImmediate: false,
    detection: {
      // order and from where user language should be detected
      order: ['querystring', 'localStorage', 'navigator', 'htmlTag'],

      // keys or params to lookup language from
      lookupQuerystring: 'lng',
      lookupCookie: 'i18next',
      lookupLocalStorage: 'i18nextLng',
      lookupFromPathIndex: 0,
      lookupFromSubdomainIndex: 0,

      // cache user language on
      caches: ['localStorage', 'cookie'],
      excludeCacheFor: ['cimode'], // languages to not persist (cookie, localStorage)

      // optional htmlTag with lang attribute, the default is:
      htmlTag: document.documentElement,
    },

    // if you see an error like: "Argument of type 'DefaultTFuncReturn' is not assignable to parameter of type xyz"
    // set returnNull to false (and also in the i18next.d.ts options)
    // returnNull: false,
  })
  .then(() => {
    const ErrorBoundaryPlugin = bugsnag.getPlugin('react');

    if (!ErrorBoundaryPlugin) {
      throw new Error('Bugsnag React plugin not loaded');
    }

    const ErrorBoundary = ErrorBoundaryPlugin.createErrorBoundary(React);
    root.render(
      <TrackingProvider>
        <ErrorBoundary FallbackComponent={FiveHunna}>
          <GlobalStyle />
          <App />
        </ErrorBoundary>
      </TrackingProvider>,
    );
  });

/*
i18n
  .use(XHR)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    debug: import.meta.env.VITE_NODE_ENV !== 'production',
    initImmediate: false,
    defaultNS: 'translation',
    backend: {
      loadPath: '/locales/{{lng}}/{{ns}}.json',
      crossDomain: false,
    },
    react: {
      useSuspense: false,
    },
    fallbackLng: 'en',
    interpolation: {
      escapeValue: false,
    },

  })
  .then(() => {
      console.log('herna')
   // const ErrorBoundary = bugsnag.getPlugin('react');
      //        <ErrorBoundary FallbackComponent={FiveHunna}>
    root.render(
      <Wrapper>
          <GlobalStyle />
          <App />
      </Wrapper>,
    );
  });
*/

const container = document.getElementById('root');
const root = createRoot(container!);


const Loading = styled.div`
    display: flex;
    height: 100vh;
    width: 100vw;
    align-items: center;
    justify-content: center;
    background-color: #fcfcfc;

    svg {
        animation: anim 1s ease-in-out infinite;
        user-select: none;
        transition-timing-function: cubic-bezier(0.75, 0.25, 0.25, 0.75);

        @keyframes anim {
            0% {
                transform: scale(0.6);
            }
            50% {
                transform: scale(0.69);
            }
            100% {
                transform: scale(0.6);
            }
        }
    }
`;

root.render(<Loading />);

// Set language header when changed
i18n.on('languageChanged', (lang) => {
  axios.defaults.headers.common['Accept-Language'] = lang;
});
